import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { api, ROLE_LABELS, STATUS_LABELS } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { RiskBadge, ProgressBar, StaffProductivityCard } from "@/components/ProjectMetrics";
import { Card } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  FolderKanban, Activity, CheckCircle2, AlertTriangle, Target, Layers, Users2, Gauge, ClipboardList,
} from "lucide-react";

export default function Dashboard() {
  const { user } = useAuth();
  if (["SUPER_ADMIN", "DIREKTUR", "MANAGER_OPERASIONAL"].includes(user?.role)) return <ExecutiveDashboard user={user} />;
  if (user?.role === "PIMPINAN_PROYEK") return <PimpinanDashboard user={user} />;
  return <StaffDashboard user={user} />;
}

function Header({ role, name }) {
  const sub = {
    SUPER_ADMIN: "Kontrol penuh sistem & seluruh data.",
    DIREKTUR: "Pemantauan kinerja bisnis & proyek.",
    MANAGER_OPERASIONAL: "Dashboard operasional proyek & produktivitas.",
    PIMPINAN_PROYEK: "Proyek yang Anda pimpin.",
    STAFF_OPERASIONAL: "Proyek & beban kerja pribadi Anda.",
  };
  return (
    <div>
      <p className="uppercase text-[10px] tracking-wider font-semibold text-muted-foreground">{ROLE_LABELS[role]} · Dashboard</p>
      <h1 className="font-heading text-3xl font-bold tracking-tight">Selamat datang, {name?.split(" ")[0]}</h1>
      <p className="text-sm text-muted-foreground mt-1">{sub[role]}</p>
    </div>
  );
}

function Kpi({ label, value, icon: Icon, color }) {
  return (
    <Card className="p-4 hover:-translate-y-[2px] hover:shadow-md transition-transform duration-200">
      <div className="flex items-center justify-between">
        <p className="uppercase text-[10px] tracking-wider font-semibold text-muted-foreground">{label}</p>
        <Icon className={`h-4 w-4 ${color}`} />
      </div>
      <p className="font-heading text-3xl font-bold mt-2 tabular-nums">{value}</p>
    </Card>
  );
}

/* ---------------- Executive (Admin / Direktur / Manager) ---------------- */
function ExecutiveDashboard({ user }) {
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const isManager = user.role === "MANAGER_OPERASIONAL";
  useEffect(() => { api.get("/dashboard/executive").then((r) => setData(r.data)).catch(() => {}); }, []);
  if (!data) return <SkeletonView />;
  const s = data.stats;

  const kpis = [
    { label: "Total Proyek", value: s.total, icon: FolderKanban, color: "text-primary" },
    { label: "Aktif", value: s.active, icon: Activity, color: "text-red-500" },
    { label: "On Track", value: s.on_track, icon: CheckCircle2, color: "text-emerald-500" },
    { label: "At Risk", value: s.at_risk, icon: AlertTriangle, color: "text-amber-500" },
    { label: "High Risk", value: s.high_risk, icon: AlertTriangle, color: "text-orange-500" },
    { label: "Delayed", value: s.delayed, icon: AlertTriangle, color: "text-red-500" },
    { label: "Selesai", value: s.completed, icon: CheckCircle2, color: "text-emerald-600" },
    { label: "Overall", value: `${s.overall_completion}%`, icon: Target, color: "text-primary" },
  ];

  return (
    <div className="space-y-6">
      <Header role={user.role} name={user.name} />
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="kpi-grid">
        {kpis.map((k, i) => (
          <motion.div key={k.label} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.04 }}><Kpi {...k} /></motion.div>
        ))}
      </div>

      <Card className="p-5">
        <p className="uppercase text-[10px] tracking-wider font-semibold text-muted-foreground flex items-center gap-1.5"><Layers className="h-3.5 w-3.5" /> Volume Arsip Keseluruhan</p>
        <p className="font-heading text-2xl font-bold mt-2 tabular-nums">{s.total_processed.toLocaleString()} <span className="text-base text-muted-foreground font-normal">/ {s.total_target.toLocaleString()}</span></p>
        <Progress value={s.overall_completion} className="h-2 mt-3" />
        <p className="text-xs text-muted-foreground mt-2">{s.overall_completion}% arsip diproses & disetujui</p>
      </Card>

      <Card className="overflow-hidden">
        <div className="p-5 border-b border-border">
          <h3 className="font-heading font-semibold text-lg">{isManager ? "Proyek Operasional" : "Monitoring Proyek"}</h3>
          <p className="text-xs text-muted-foreground">Diurutkan berdasarkan risiko tertinggi</p>
        </div>
        <div className="overflow-x-auto scrollbar-thin">
          <Table>
            <TableHeader>
              <TableRow className="bg-muted/50">
                <TableHead>Proyek</TableHead><TableHead>Klien</TableHead><TableHead>Pimpinan</TableHead>
                {(isManager || user.role === "SUPER_ADMIN") && <TableHead>Staff</TableHead>}
                <TableHead>Target Selesai</TableHead><TableHead className="min-w-[160px]">Progress</TableHead>
                <TableHead>Target Progress</TableHead>
                <TableHead>Estimasi Selesai</TableHead><TableHead>Estimasi Telat</TableHead><TableHead>Risiko</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.projects.length === 0 && <TableRow><TableCell colSpan={11} className="text-center text-muted-foreground py-10">Belum ada proyek.</TableCell></TableRow>}
              {data.projects.map((p) => (
                <TableRow key={p.id} className="cursor-pointer hover:bg-accent/50" onClick={() => nav(`/projects/${p.id}`)} data-testid={`dash-project-${p.code}`}>
                  <TableCell><p className="font-semibold">{p.name}</p><p className="text-xs text-muted-foreground">{p.code}</p></TableCell>
                  <TableCell className="text-sm">{p.client}</TableCell>
                  <TableCell className="text-sm">{p.pimpinan_name || <span className="text-muted-foreground">-</span>}</TableCell>
                  {(isManager || user.role === "SUPER_ADMIN") && <TableCell className="text-sm tabular-nums">{p.staff_count}</TableCell>}
                  <TableCell className="text-sm tabular-nums">{p.target_completion_date}</TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <Progress value={p.metrics.completion_pct} className="h-1.5 w-24" />
                      <span className="text-xs tabular-nums font-medium">{p.metrics.completion_pct}%</span>
                    </div>
                  </TableCell>
                  <TableCell className="text-sm tabular-nums">{p.metrics.expected_pct}%</TableCell>
                  <TableCell className="text-sm tabular-nums">{p.metrics.expected_completion || "-"}</TableCell>
                  <TableCell className="text-sm tabular-nums">{p.metrics.estimated_delay_days > 0 ? `${p.metrics.estimated_delay_days} hari` : "-"}</TableCell>
                  <TableCell><RiskBadge risk={p.metrics.risk} /></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </Card>
    </div>
  );
}

/* ---------------- Pimpinan Proyek ---------------- */
function PimpinanDashboard({ user }) {
  const nav = useNavigate();
  const [projects, setProjects] = useState(null);
  useEffect(() => { api.get("/projects").then((r) => setProjects(r.data)).catch(() => {}); }, []);
  if (!projects) return <SkeletonView />;

  const totalTarget = projects.reduce((a, p) => a + (p.metrics.total_target || 0), 0);
  const totalProcessed = projects.reduce((a, p) => a + (p.metrics.processed || 0), 0);
  const atRisk = projects.filter((p) => ["ORANGE", "RED"].includes(p.metrics.risk) && p.status !== "COMPLETED").length;
  const overall = totalTarget ? Math.round((totalProcessed / totalTarget) * 100) : 0;

  return (
    <div className="space-y-6">
      <Header role={user.role} name={user.name} />
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Kpi label="Proyek Saya" value={projects.length} icon={FolderKanban} color="text-primary" />
        <Kpi label="Target Arsip" value={totalTarget.toLocaleString()} icon={Target} color="text-red-500" />
        <Kpi label="Diproses" value={totalProcessed.toLocaleString()} icon={CheckCircle2} color="text-emerald-500" />
        <Kpi label="Perlu Perhatian" value={atRisk} icon={AlertTriangle} color="text-orange-500" />
      </div>
      <ProjectCardsGrid projects={projects} nav={nav} showStaff />
      {projects.length > 0 && (
        <div className="space-y-4">
          <h3 className="font-heading font-semibold">Produktivitas Staff per Proyek</h3>
          <div className="grid md:grid-cols-2 gap-4">
            {projects.map((p) => <StaffProductivityCard key={p.id} projectId={p.id} />)}
          </div>
        </div>
      )}
    </div>
  );
}

/* ---------------- Staff Operasional ---------------- */
function StaffDashboard({ user }) {
  const nav = useNavigate();
  const [projects, setProjects] = useState(null);
  const [prod, setProd] = useState(null);
  useEffect(() => {
    api.get("/projects").then((r) => setProjects(r.data)).catch(() => {});
    api.get("/productivity/me").then((r) => setProd(r.data)).catch(() => {});
  }, []);
  if (!projects) return <SkeletonView />;
  const pct = prod && prod.target ? Math.min(100, Math.round((prod.today / prod.target) * 100)) : 0;

  return (
    <div className="space-y-6">
      <Header role={user.role} name={user.name} />
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Kpi label="Proyek Ditugaskan" value={projects.length} icon={FolderKanban} color="text-primary" />
        <Kpi label="Disetujui Hari Ini" value={prod ? `${prod.today_ml} mL` : "-"} icon={Gauge} color="text-emerald-500" />
        <Kpi label="Target Harian" value={prod ? `${prod.target_ml} mL` : "-"} icon={Target} color="text-red-500" />
        <Kpi label="Total Disetujui" value={prod ? `${prod.total_ml} mL` : "-"} icon={ClipboardList} color="text-orange-500" />
      </div>
      <Card className="p-5">
        <div className="flex items-center justify-between mb-2">
          <h3 className="font-heading font-semibold">Beban Kerja Hari Ini</h3>
          <span className="text-sm font-semibold tabular-nums">{prod ? `${prod.today} / ${prod.target}` : "-"}</span>
        </div>
        <Progress value={pct} className="h-3" />
        <p className="text-xs text-muted-foreground mt-2">{pct}% target harian tercapai</p>
      </Card>
      <div>
        <h3 className="font-heading font-semibold mb-3">Proyek Saya</h3>
        <ProjectCardsGrid projects={projects} nav={nav} />
      </div>
    </div>
  );
}

function ProjectCardsGrid({ projects, nav, showStaff }) {
  if (projects.length === 0) return (
    <Card className="p-16 text-center"><FolderKanban className="h-10 w-10 mx-auto text-muted-foreground" /><p className="mt-3 text-muted-foreground">Anda belum ditugaskan ke proyek.</p></Card>
  );
  return (
    <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">
      {projects.map((p, i) => (
        <motion.div key={p.id} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.03 }}>
          <Card className="p-5 cursor-pointer hover:-translate-y-[3px] hover:shadow-md transition-transform duration-200 h-full flex flex-col"
            onClick={() => nav(`/projects/${p.id}`)} data-testid={`dash-project-${p.code}`}>
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <p className="font-mono text-[11px] text-primary font-semibold">{p.code}</p>
                <h3 className="font-heading font-semibold text-lg leading-tight truncate">{p.name}</h3>
                <p className="text-xs text-muted-foreground truncate">{p.client}</p>
              </div>
              <Badge variant="secondary" className="shrink-0">{STATUS_LABELS[p.status]}</Badge>
            </div>
            <div className="mt-4 flex-1"><ProgressBar m={p.metrics} /></div>
            <div className="mt-3 grid grid-cols-2 gap-2 text-xs text-muted-foreground">
              <span>Mulai: {p.start_date}</span>
              <span className="text-right">Target: {p.target_completion_date}</span>
              {showStaff && <span className="flex items-center gap-1"><Users2 className="h-3 w-3" /> {(p.staff_ids || []).length} staff</span>}
              <span className={showStaff ? "text-right" : "col-span-2 text-right"}>{p.metrics.processed}/{p.metrics.total_target} berkas</span>
            </div>
            <div className="mt-3 flex justify-end"><RiskBadge risk={p.metrics.risk} /></div>
          </Card>
        </motion.div>
      ))}
    </div>
  );
}

function SkeletonView() {
  return (
    <div className="space-y-6">
      <Skeleton className="h-10 w-64" />
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">{Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-24" />)}</div>
      <Skeleton className="h-72" />
    </div>
  );
}
